// ResourcesOverviewPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Grid, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const ResourcesOverviewPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Resources', path: '/resources' },
    { name: 'Resources Overview', path: '/resources' },
  ];

  const resourceSections = [
    {
      title: 'Our Blog',
      description: 'Stay updated with the latest news, market trends, recycling tips, and industry insights from our experts. Our blog is a valuable source of information for both new and experienced traders.',
      items: [
        'Market Analysis & Forecasts',
        'Sustainable Practices & Innovations',
        'Company News & Updates',
      ],
      link: '/resources/blog',
      linkText: 'Visit Our Blog',
    },
    {
      title: 'Frequently Asked Questions (FAQs)',
      description: 'Find quick answers to common questions about our platform, trading processes, account management, and more. Our comprehensive FAQ section is designed to help you navigate with ease.',
      items: [
        'Account Setup & Verification',
        'Buying & Selling Processes',
        'Payment & Logistics',
      ],
      link: '/resources/faqs',
      linkText: 'Browse FAQs',
    },
    {
      title: 'Glossary of Terms',
      description: 'Demystify the jargon of the scrap and recycling industry with our extensive glossary. Understand key terms, material classifications, and industry-specific vocabulary.',
      items: [
        'Metal Types & Grades',
        'Recycling Processes',
        'Industry Regulations',
      ],
      link: '/resources/glossary',
      linkText: 'Explore the Glossary',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Resources Overview
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Our comprehensive resources section is designed to empower you with knowledge and tools for successful and sustainable scrap trading. Whether you're new to the industry or a seasoned professional, you'll find valuable insights here.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {resourceSections.map((section, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {section.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {section.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Highlights:
                    </Typography>
                    <List dense disablePadding>
                      {section.items.map((item, itemIndex) => (
                        <ListItem key={itemIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={item} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                    {section.link && (
                      <MuiLink component={RouterLink} to={section.link} sx={{ mt: 2, display: 'inline-block', fontWeight: 500 }}>
                        {section.linkText} &rarr;
                      </MuiLink>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ResourcesOverviewPage;
